/**
 * Year-and-month selection reconciliation shared by the period pickers.
 * Pickers translate their domain data (real `TimePeriod` rows, mock strings,
 * etc.) into `PeriodYearGroup`s and pass them in alongside the raw values
 * from `MultiSelect`.
 *
 * Result is unsorted; callers apply their own ordering.
 */

export type PeriodYearGroup = {
  yearValue: string;
  monthValues: string[];
};

export function reconcilePeriodSelection(
  newValues: string[],
  prevValues: string[],
  yearGroups: PeriodYearGroup[]
): string[] {
  const next = new Set(newValues);
  const prev = new Set(prevValues);

  for (const { yearValue, monthValues } of yearGroups) {
    const wasYearSelected = prev.has(yearValue);
    const isYearSelected = next.has(yearValue);
    const allMonthsNow = monthValues.every((m) => next.has(m));
    const allMonthsBefore = monthValues.every((m) => prev.has(m));
    const monthsNowSelected = monthValues.filter((m) => next.has(m));

    if (!wasYearSelected && !isYearSelected && allMonthsNow) {
      // Selecting all 12 months collapses into the year.
      next.add(yearValue);
      monthValues.forEach((m) => next.delete(m));
    } else if (allMonthsBefore && !allMonthsNow) {
      // Deselecting any month while all were selected drops the year too.
      next.delete(yearValue);
    } else if (!wasYearSelected && isYearSelected && monthsNowSelected.length) {
      // Year just toggled on — drop redundant standalone months for it.
      monthValues.forEach((m) => next.delete(m));
    } else if (wasYearSelected && !isYearSelected) {
      // Year just toggled off — drop its months too.
      monthValues.forEach((m) => next.delete(m));
    } else if (isYearSelected && monthsNowSelected.length) {
      // Clicking a month inside a selected year: expand the year into all
      // other months, leaving the clicked one out (partial-year selection).
      next.delete(yearValue);
      monthsNowSelected.forEach((m) => next.delete(m));
      monthValues
        .filter((m) => !monthsNowSelected.includes(m))
        .forEach((m) => next.add(m));
    }
  }

  return Array.from(next);
}
